import React, { useState, useEffect, useRef } from "react";
import { useHighlightKey } from "../context/HighlightKeyContext";

const keys = [
  { note: "c4", type: "white" },
  { note: "c-4", type: "black", positionIndex: 0 },
  { note: "d4", type: "white" },
  { note: "d-4", type: "black", positionIndex: 1 },
  { note: "e4", type: "white" },
  { note: "f4", type: "white" },
  { note: "f-4", type: "black", positionIndex: 3 },
  { note: "g4", type: "white" },
  { note: "g-4", type: "black", positionIndex: 4 },
  { note: "a5", type: "white" },
  { note: "a-5", type: "black", positionIndex: 5 },
  { note: "b5", type: "white" },
  { note: "c5", type: "white" },
];

const whiteKeysCount = keys.filter((k) => k.type === "white").length;

const sequences = [
  { name: "Experience", notes: ["e4", "g4", "c4"], targetId: "experience" },
  { name: "Skills", notes: ["g4", "a5", "c5"], targetId: "skills" },
  { name: "Projects", notes: ["c4", "e4", "d4"], targetId: "projects" },
];

export default function Piano() {
  const containerRef = useRef(null);
  const { highlightedNote, setHighlightedNote } = useHighlightKey();

  const [whiteKeyWidth, setWhiteKeyWidth] = useState(60);
  const [whiteKeyHeight, setWhiteKeyHeight] = useState(200);
  const [blackKeyWidth, setBlackKeyWidth] = useState(40);
  const [blackKeyHeight, setBlackKeyHeight] = useState(120);

  const [activeSequence, setActiveSequence] = useState(null);
  const [progressIndex, setProgressIndex] = useState(0);
  const playedNotesRef = useRef([]);

  // Web Audio API refs
  const audioContext = useRef(null);
  const audioBuffers = useRef({});

  // Load sounds + handle key sizing
  useEffect(() => {
    const updateSizes = () => {
      if (!containerRef.current) return;
      const parentWidth = containerRef.current.parentElement.clientWidth;
      const wKeyWidth = parentWidth / whiteKeysCount;
      const wKeyHeight = wKeyWidth * (200 / 60);
      const bKeyWidth = wKeyWidth * (2 / 3);
      const bKeyHeight = wKeyHeight * (120 / 200);
      setWhiteKeyWidth(wKeyWidth);
      setWhiteKeyHeight(wKeyHeight);
      setBlackKeyWidth(bKeyWidth);
      setBlackKeyHeight(bKeyHeight);
    };

    updateSizes();

    // Initialize Web Audio
    audioContext.current = new (window.AudioContext ||
      window.webkitAudioContext)();

    // preload audio
    keys.forEach((k) => {
      const note = k.note.toLowerCase();
      const audio = new Audio(`/notes/${note}.mp3`); // starts from root
      audio.load();
      audioBuffers.current[note] = audio;
    });

    let resizeObserver;
    if (containerRef.current && window.ResizeObserver) {
      resizeObserver = new ResizeObserver(updateSizes);
      resizeObserver.observe(containerRef.current.parentElement);
    } else {
      window.addEventListener("resize", updateSizes);
    }

    return () => {
      if (resizeObserver && containerRef.current) {
        resizeObserver.unobserve(containerRef.current.parentElement);
      } else {
        window.removeEventListener("resize", updateSizes);
      }
    };
  }, []);

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  // Play note using Web Audio API
  const playNote = (note) => {
    const audio = audioBuffers.current[note.toLowerCase()];
    if (!audio) return;

    // Play a fresh instance
    const clone = audio.cloneNode();
    clone.play();

    // Handle sequences as before
    if (!activeSequence) return;

    playedNotesRef.current.push(note.toLowerCase());

    const seq = sequences.find((s) => s.name === activeSequence);
    const expectedNote = seq.notes[progressIndex];

    if (note.toLowerCase() === expectedNote.toLowerCase()) {
      const newIndex = progressIndex + 1;
      setProgressIndex(newIndex);

      if (newIndex === seq.notes.length) {
        playedNotesRef.current = [];

        setTimeout(() => {
          setProgressIndex(0);
          scrollToSection(seq.targetId);
          setHighlightedNote(seq.notes[0]);
        }, 350);
      } else {
        setHighlightedNote(seq.notes[newIndex]);
      }
    } else {
      setProgressIndex(0);
      setHighlightedNote(seq.notes[0]);
      playedNotesRef.current = [];
    }
  };

  const handleSequenceSelect = (sequenceName) => {
    setActiveSequence(sequenceName);
    setProgressIndex(0);
    const seq = sequences.find((s) => s.name === sequenceName);
    setHighlightedNote(seq.notes[0]);
  };

  return (
    <div className="flex flex-col items-center gap-4">
      {/* Sequence buttons */}
      <div className="flex gap-2 mt-4">
        {sequences.map((seq) => (
          <button
            key={seq.name}
            onClick={() => handleSequenceSelect(seq.name)}
            className={`px-4 py-1 rounded hover:bg-gray-300 ${
              activeSequence === seq.name
                ? "bg-yellow-400 text-black"
                : "bg-gray-200"
            }`}
          >
            {seq.name}
          </button>
        ))}
      </div>

      {/* Progress display */}
      <div className="mt-2 flex gap-2">
        {activeSequence ? (
          sequences
            .find((s) => s.name === activeSequence)
            .notes.map((note, index) => (
              <span
                key={index}
                className={`${
                  index < progressIndex
                    ? "font-bold text-green-600"
                    : "text-gray-400"
                }`}
              >
                {note.toUpperCase()}
              </span>
            ))
        ) : (
          <span className="text-gray-400 italic">No theme selected</span>
        )}
      </div>

      {/* Piano */}
      <div
        ref={containerRef}
        className="relative mx-auto select-none"
        style={{
          width: whiteKeyWidth * whiteKeysCount,
          height: whiteKeyHeight,
        }}
      >
        {/* White keys */}
        {keys
          .filter((k) => k.type === "white")
          .map((key) => {
            const isHighlighted =
              highlightedNote &&
              key.note.toLowerCase() === highlightedNote.toLowerCase();

            return (
              <div
                key={key.note}
                onClick={() => playNote(key.note)}
                className={`inline-block border box-border relative cursor-pointer
          ${
            isHighlighted
              ? "bg-yellow-300 hover:bg-yellow-400"
              : "bg-white hover:bg-gray-200"
          }
          transition-colors duration-150
        `}
                style={{
                  width: whiteKeyWidth,
                  height: whiteKeyHeight,
                }}
              >
                <div className="absolute bottom-2 w-full text-center font-bold text-xs">
                  {key.note.toUpperCase()}
                </div>
              </div>
            );
          })}

        {/* Black keys */}
        {keys
          .filter((k) => k.type === "black")
          .map((key) => {
            const left =
              whiteKeyWidth * (key.positionIndex + 1) - blackKeyWidth / 2;
            const isHighlighted =
              highlightedNote &&
              key.note.toLowerCase() === highlightedNote.toLowerCase();

            return (
              <div
                key={key.note}
                onClick={() => playNote(key.note)}
                className={`absolute top-0 z-20 cursor-pointer rounded-b-md border
          ${
            isHighlighted
              ? "bg-yellow-400 hover:bg-yellow-500"
              : "bg-black hover:bg-gray-800"
          }
          transition-colors duration-150
        `}
                style={{
                  width: blackKeyWidth,
                  height: blackKeyHeight,
                  left,
                }}
              />
            );
          })}
      </div>
    </div>
  );
}
